import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter } from "react-router-dom";

import { App } from "./App.js";
import { SessionProvider } from "./session/SessionContext.js";
import "./styles.css";

/**
 * Entry point.
 *
 * `VITE_API_BASE_URL` must name the API host in production, and `.env.production`
 * is where it is written down.
 *
 * This comment used to say the opposite — that the value is empty in production
 * because the two share a registrable domain and relative paths therefore work.
 * They share a domain but not an origin: the dashboard is served by nginx from a
 * static root with no proxy in front of the API, so a relative `POST /auth/...`
 * lands on `try_files` and comes back **405**. The session cookie crosses between
 * the two hosts because it is scoped to the parent domain, which is what made the
 * wrong explanation sound plausible.
 *
 * Empty is still correct in development, where the Vite dev server proxies to a
 * locally running API — hence the fallback rather than a required value.
 */
const baseUrl = import.meta.env.VITE_API_BASE_URL ?? "";

const container = document.getElementById("root");

if (container === null) {
  throw new Error("The #root element is missing from index.html.");
}

createRoot(container).render(
  <StrictMode>
    <BrowserRouter>
      <SessionProvider baseUrl={baseUrl}>
        <App />
      </SessionProvider>
    </BrowserRouter>
  </StrictMode>,
);
